import React, { useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import { TwoGisGeoCoder, TwoGisMap } from '@douleutarasgr/2gis-plugin';
import { useTranslation } from 'react-i18next';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { createGisFromTwoGis } from 'Douleutaras/components/Forms/twoGisModel';
import { CYPRUS_MAP_CENTER, twoGisGeocoderOptions, twoGisMapOptions } from 'Douleutaras/configuration/twoGis';

import { MapLoader } from './MapLoader';
import './LocationMap.scss';

const PIN_ICON = `${process.env.ASSETS_CDN_PATH}/douleutaras/img/douleutaras/pinplace.png`;

// The 2GIS map must only be initialized once its container is actually visible,
// otherwise mapgl fails to size itself. Mirrors the 100ms guard used by the
// dynamic-forms 2GIS provider.
const MAP_INIT_DELAY = 100;

interface Coordinates {
  lat: number;
  lng: number;
}

interface TwoGisLocationMapProps {
  // Initial map center. When absent, the Cyprus default center is used.
  center?: Coordinates;
  // When true, the initial `center` is reverse-geocoded and emitted through
  // `onChange` so a device-location seed gets validated immediately.
  resolveCenter?: boolean;
  onChange(gis: Gis): void;
}

// 2GIS works in `[longitude, latitude]` order, the opposite of Google's `{ lat, lng }`.
const toLngLat = (coordinates: Coordinates): [number, number] => [coordinates.lng, coordinates.lat];

/**
 * 2GIS counterpart of {@link GoogleLocationMap}. A pin is locked to the map center
 * (`enableCenterPinLogic`, run automatically by `loadMap`); once dragging settles, the
 * center is reverse-geocoded and emitted through `onChange`.
 */
export const TwoGisLocationMap = ({ center, resolveCenter, onChange }: TwoGisLocationMapProps) => {
  const { t } = useTranslation();

  // tslint:disable-next-line:no-null-keyword
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<TwoGisMap | undefined>(undefined);
  const geocoderRef = useRef<TwoGisGeoCoder | undefined>(undefined);
  // Keep the latest onChange without re-subscribing the map's position stream.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Guards against reverse-geocoding the same device-location center more than once.
  const resolveGuardRef = useRef(false);

  const [ isLoaded, setIsLoaded ] = useState(false);
  const [ hasInteracted, setHasInteracted ] = useState(false);

  const reverseGeocode = (lngLat: Array<number>): void => {
    const geocoder = geocoderRef.current;
    if (!geocoder) {
      return;
    }
    geocoder.geocode({ lat: `${lngLat[1]}`, lon: `${lngLat[0]}` })
      .subscribe(items => {
        const gis = createGisFromTwoGis(items);
        if (gis) {
          onChangeRef.current(gis);
        }
      });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    geocoderRef.current = new TwoGisGeoCoder(twoGisGeocoderOptions);
    const map = new TwoGisMap(container, twoGisMapOptions);
    map.markerOptions = { icon: PIN_ICON };
    // init() wires up `positionChangedObservable` and resolves the container element;
    // it must run before loadMap()/subscribing. Mirrors the provider's initMap() call.
    map.init();
    mapRef.current = map;

    const initialCenter = center ? toLngLat(center) : CYPRUS_MAP_CENTER;
    let positionSubscription: Subscription | undefined;

    const timer = setTimeout(() => {
      map.loadMap(initialCenter)
        .then(() => {
          setIsLoaded(true);
          map.addMarker(initialCenter);
          map.instance.on('movestart', () => setHasInteracted(true));

          // enableCenterPinLogic (run by loadMap) emits the settled center here.
          positionSubscription = map.positionChangedObservable.subscribe(lngLat => reverseGeocode(lngLat));

          if (center && resolveCenter && !resolveGuardRef.current) {
            resolveGuardRef.current = true;
            reverseGeocode(initialCenter);
          }
        });
    }, MAP_INIT_DELAY);

    return () => {
      clearTimeout(timer);
      positionSubscription?.unsubscribe();
      map.destroy();
      mapRef.current = undefined;
      geocoderRef.current = undefined;
    };
  }, []);

  // Re-center the map when a new center is provided from outside (e.g., after an address
  // is picked via the autocomplete input), and validate a device-location seed.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.instance || !center) {
      return;
    }
    const lngLat = toLngLat(center);
    map.instance.setCenter(lngLat);
    map.removeMarkers();
    map.addMarker(lngLat);

    if (resolveCenter && !resolveGuardRef.current) {
      resolveGuardRef.current = true;
      reverseGeocode(lngLat);
    }
  }, [center, resolveCenter]);

  // Reset the resolve guard once the parent stops requesting an initial resolve.
  useEffect(() => {
    if (!resolveCenter) {
      resolveGuardRef.current = false;
    }
  }, [resolveCenter]);

  return (
    <div styleName="location-map">
      <div styleName="location-map__inner">
        <div styleName="location-map__canvas" ref={containerRef} />
        { !isLoaded &&
          <MapLoader />
        }
        { isLoaded && !hasInteracted &&
          <div styleName="location-map__hint">
            {t('T.POST-JOB.MAP.MOVE-HINT', 'Move the map to pinpoint the exact location you are looking for')}
          </div>
        }
      </div>
    </div>
  );
};

export default TwoGisLocationMap;
